import { useQuery } from "@apollo/client";
import { AUDIT_LOGS_QUERY } from "@/graphql/queries";
import SecondaryNavigation from "@/components/navigation/SecondaryNavigation";
import { getNavWithCurrent } from "@/components/utils/settingsNavUtils";
import {
  CheckIcon,
  HandThumbUpIcon,
  UserIcon,
} from "@heroicons/react/20/solid";
import { classNames } from "@/components/utils/functions";
import { AuditType } from "@/types/AuditType";
import dayjs from "dayjs";
import AdminGuard from "@/components/guards/AdminGuard";

function AuditLogsComponent() {
  const { data, loading, error } = useQuery(AUDIT_LOGS_QUERY, {
    variables: { limit: 50, offset: 0 },
  });

  if (loading)
    return (
      <div className="min-h-screen bg-gray-100">
        <SecondaryNavigation items={getNavWithCurrent("Audit Logs")} />
        <main className="max-w-7xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
          <div className="bg-white shadow rounded-lg p-6">
            <h1 className="text-2xl font-semibold mb-4 text-indigo-700">
              Audit Log
            </h1>
            <div className="text-gray-500">Loading...</div>
          </div>
        </main>
      </div>
    );

  return (
    <main>
      <div className="mx-auto max-w-7xl lg:flex lg:gap-x-16 lg:px-8">
        <h1 className="sr-only">Audit Log</h1>

        <SecondaryNavigation items={getNavWithCurrent("Audit Logs")} />

        <main className="px-4 py-16 sm:px-6 lg:px-0 lg:py-10 w-full">
          <section className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700 w-full">
            <div className="sm:flex sm:items-center">
              <div className="sm:flex-auto">
                <h1 className="text-base font-semibold leading-6 text-gray-900 dark:text-gray-100">
                  Audit Log
                </h1>
                <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                  A list of all the logs in your organisation.
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
                <button
                  type="button"
                  className="block rounded-md bg-indigo-600 dark:bg-indigo-500 px-3 py-2 text-center text-sm font-semibold text-white dark:text-gray-900 shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  onClick={() => null}
                >
                  Export
                </button>
              </div>
            </div>
            {error && (
              <div className="text-red-600 mt-4">Error: {error.message}</div>
            )}
            {loading && <div className="text-gray-500 mt-4">Loading...</div>}
            {data?.getLogs?.length === 0 ? (
              <div className="text-gray-500 mt-4">No logs found</div>
            ) : (
              <div className="mt-8">
                <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                  <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                    <ul role="list" className="mb-8">
                      {data?.getLogs?.map((log: AuditType) => (
                        <li key={log?.id}>
                          <div className="relative pb-8">
                            {data?.getLogs?.length !== log?.id ? (
                              <span
                                aria-hidden="true"
                                className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                              />
                            ) : null}
                            <div className="relative flex space-x-3">
                              <div>
                                <span
                                  className={classNames(
                                    "flex size-8 items-center justify-center rounded-full ring-8 ring-inset ring-gray-200 dark:ring-gray-700"
                                  )}
                                >
                                  <UserIcon
                                    aria-hidden="true"
                                    className="size-5 text-gray-900 dark:text-gray-100"
                                  />
                                </span>
                              </div>
                              <div className="flex flex-col min-w-0 flex-1 space-x-4 pt-1.5 ml-0">
                                <div>
                                  <p className="text-sm font-semibold text-indigo-700 dark:text-indigo-400">
                                    {log?.action}
                                  </p>
                                  {log?.comment && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                      {log.comment}
                                    </p>
                                  )}
                                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                    {log?.auditableType}
                                  </p>
                                </div>
                                <code className="text-xs xs:text-base inline-flex text-left items-center space-x-4 bg-gray-800 text-white rounded-lg p-4">
                                  <span className="flex">
                                    <span className="flex-1">
                                      <span>
                                        {JSON.stringify(log?.auditedChanges)}
                                      </span>
                                    </span>
                                  </span>
                                </code>

                                <div className="whitespace-nowrap text-right text-sm text-gray-500 dark:text-gray-400">
                                  <time dateTime={log?.createdAt}>
                                    {dayjs(log?.createdAt).format(
                                      "DD/MM/YYYY HH:mm"
                                    )}
                                  </time>
                                </div>
                              </div>
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>
    </main>
  );
}

export default function AuditLogs() {
  return (
    <AdminGuard>
      <AuditLogsComponent />
    </AdminGuard>
  );
}
